import { Loader2, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import LegalAcceptanceCheckbox from "@/components/LegalAcceptanceCheckbox";
import { formatLicenseDateForDisplay } from "@/lib/licenseDates";

/**
 * Aceptación legal compartida — obligatoria antes de enviar por web o PDF.
 * En ampliaciones con aceptación vigente reutilizable, muestra leyenda informativa.
 */
export default function LicenseCommercialLegalSection({
  legalCatalog = null,
  legalLoading = false,
  legalError = "",
  legalAccepted = false,
  legalLocked = false,
  legalSaving = false,
  legalAcceptError = "",
  agreementAcceptanceId = "",
  legalReused = false,
  reusedAcceptance = null,
  onLegalAcceptedChange,
  disabled = false,
}) {
  if (legalLoading) {
    return (
      <p
        className="text-[11px] text-slate-500 flex items-center gap-2 m-0"
        data-testid="license-commercial-legal-loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Cargando documentos legales…
      </p>
    );
  }

  if (legalError) {
    return (
      <div
        className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-950"
        data-testid="license-commercial-legal-error"
      >
        {legalError}
      </div>
    );
  }

  const version = reusedAcceptance?.contract_version || legalCatalog?.contract_version || "—";
  const acceptedAt = reusedAcceptance?.accepted_at
    ? formatLicenseDateForDisplay(String(reusedAcceptance.accepted_at).slice(0, 10))
    : "—";

  if (legalReused && agreementAcceptanceId) {
    return (
      <div
        className="rounded-md border border-emerald-200 bg-emerald-50/80 px-3 py-3 space-y-2"
        data-testid="license-commercial-legal-reused"
      >
        <p className="text-xs font-semibold text-emerald-950 m-0 flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5 shrink-0" />
          Términos y condiciones ya aceptados
        </p>
        <p className="text-[11px] text-emerald-900 m-0">
          Versión {version} · Aceptados el {acceptedAt}
        </p>
        <p className="text-[10px] text-emerald-800 m-0">
          Términos y condiciones ya aceptados el {acceptedAt}, versión {version}.
        </p>
        <Link
          to="/legal/documentos"
          className="inline-flex text-[11px] font-semibold text-[#0F2D5C] underline underline-offset-2"
          data-testid="license-commercial-legal-reused-documents-link"
        >
          Consultar documentos legales
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="license-commercial-legal-section">
      <LegalAcceptanceCheckbox
        checked={legalLocked || legalAccepted}
        onCheckedChange={onLegalAcceptedChange}
        disabled={disabled || legalLocked || legalSaving || !legalCatalog}
        locked={legalLocked}
        contractVersion={legalCatalog?.contract_version}
        documents={legalCatalog?.documents || []}
      />
      {legalSaving ? (
        <p className="text-[10px] text-slate-500 flex items-center gap-1.5 m-0">
          <Loader2 className="w-3 h-3 animate-spin" />
          Registrando aceptación legal…
        </p>
      ) : null}
      {legalAcceptError ? (
        <p className="text-[10px] text-red-700 m-0" data-testid="license-commercial-legal-accept-error">
          {legalAcceptError}
        </p>
      ) : null}
      {legalLocked && agreementAcceptanceId && !legalReused ? (
        <p
          className="text-[10px] text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-sm px-2.5 py-2 m-0 flex items-start gap-1.5"
          data-testid="license-commercial-legal-locked"
        >
          <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            Aceptación registrada y bloqueada para validación posterior. Referencia:{" "}
            <span className="font-mono">{agreementAcceptanceId.slice(0, 8)}…</span>
          </span>
        </p>
      ) : null}
    </div>
  );
}
